import {
  lazy,
  Suspense,
  Component,
  type ReactNode,
  type ErrorInfo,
  useState,
  useEffect,
} from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import RequireRole from '@/components/auth/RequireRole';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNotificationContext } from '@/context/NotificationContext';
import NotificationToast from '@/components/shared/NotificationToast';
import { hasUnsavedWork } from '@/lib/unsavedWork';

// Lazy-load page components
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const InquiryListPage = lazy(() => import('@/pages/inquiries/InquiryListPage'));
const NewInquiryPage = lazy(() => import('@/pages/inquiries/NewInquiryPage'));
const InquiryDetailPage = lazy(() => import('@/pages/inquiries/InquiryDetailPage'));
const EditInquiryPage = lazy(() => import('@/pages/inquiries/EditInquiryPage'));
const AppointmentsPage = lazy(() => import('@/pages/appointments/AppointmentsPage'));
const JobListPage = lazy(() => import('@/pages/jobs/JobListPage'));
const JobDetailPage = lazy(() => import('@/pages/jobs/JobDetailPage'));
const WorkOrderPage = lazy(() => import('@/pages/jobs/WorkOrderPage'));
const StageFormPage = lazy(() => import('@/pages/jobs/StageFormPage'));
const SearchPage = lazy(() => import('@/pages/search/SearchPage'));
const AccountingExportPage = lazy(() => import('@/pages/accounting/AccountingExportPage'));
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'));
const CatalogPage = lazy(() => import('@/pages/settings/CatalogPage'));
const ColorCatalogAdminPage = lazy(() => import('@/pages/settings/ColorCatalogAdminPage'));
const ClearCoatCatalogPage = lazy(() => import('@/pages/settings/ClearCoatCatalogPage'));
const UsersPage = lazy(() => import('@/pages/settings/UsersPage'));
const CampaignsPage = lazy(() => import('@/pages/settings/CampaignsPage'));
const SuppliersPage = lazy(() => import('@/pages/settings/SuppliersPage'));
const IntegrationPage = lazy(() => import('@/pages/settings/IntegrationPage'));
const VersionPage = lazy(() => import('@/pages/settings/VersionPage'));
const QuotationListPage = lazy(() => import('@/pages/quotations/QuotationListPage'));
const NewQuotationPage = lazy(() => import('@/pages/quotations/NewQuotationPage'));
const QuotationDetailPage = lazy(() => import('@/pages/quotations/QuotationDetailPage'));
const CustomerListPage = lazy(() => import('@/pages/customers/CustomerListPage'));
const CustomerDetailPage = lazy(() => import('@/pages/customers/CustomerDetailPage'));
const FinancePage = lazy(() => import('@/pages/finance/FinancePage'));
const SalespersonDetailPage = lazy(() => import('@/pages/finance/SalespersonDetailPage'));

// Shop floor pages (LEAD / TECH)
const MobileDashboardPage = lazy(() => import('@/pages/dashboard/MobileDashboardPage'));
const MobileJobListPage = lazy(() => import('@/pages/jobs/MobileJobListPage'));
const FloorViewPage = lazy(() => import('@/pages/jobs/FloorViewPage'));
const MyTasksPage = lazy(() => import('@/pages/jobs/MyStagesPage'));
const TaskHistoryPage = lazy(() => import('@/pages/jobs/TaskHistoryPage'));
const PendingItemsPage = lazy(() => import('@/pages/pending-items/PendingItemsPage'));
const TaskReportPage = lazy(() => import('@/pages/reports/TaskReportPage'));
const JobSummaryPage = lazy(() => import('@/pages/reports/JobSummaryPage'));
const KpiPage = lazy(() => import('@/pages/dashboard/KpiPage'));
const CommunicationPage = lazy(() => import('@/pages/communication/CommunicationPage'));
const MessageDetailPage = lazy(() => import('@/pages/communication/MessageDetailPage'));
const TemplateEditPage = lazy(() => import('@/pages/communication/TemplateEditPage'));
const WhatsAppSetupPage = lazy(() => import('@/pages/settings/WhatsAppSetupPage'));
const WhatsAppBroadcastPage = lazy(() => import('@/pages/settings/WhatsAppBroadcastPage'));
const WarrantyClaimListPage = lazy(() => import('@/pages/warranty-claims/WarrantyClaimListPage'));
const WarrantyClaimDetailPage = lazy(
  () => import('@/pages/warranty-claims/WarrantyClaimDetailPage'),
);
const WarrantyListPage = lazy(() => import('@/pages/warranties/WarrantyListPage'));

// Customer portal pages
const PortalPage = lazy(() => import('@/pages/portal/PortalPage'));
const PortalJobListPage = lazy(() => import('@/pages/portal/PortalJobListPage'));
const PortalWarrantyClaimsPage = lazy(() => import('@/pages/portal/PortalWarrantyClaimsPage'));
const PortalWarrantyClaimDetailPage = lazy(
  () => import('@/pages/portal/PortalWarrantyClaimDetailPage'),
);

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    </div>
  );
}

function NotFound() {
  return (
    <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
      Page not found.
    </div>
  );
}

function isChunkLoadError(error: Error): boolean {
  const msg = error.message || '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    msg.includes('is not a valid JavaScript MIME type') ||
    (error.name === 'TypeError' && msg.includes('Failed to fetch'))
  );
}

class PageErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Page render error:', error, info);
    // Reload to pick up the fresh chunk ONLY when there is no unsaved work;
    // otherwise the reload would wipe a half-filled form / staged photos. When
    // dirty we render a manual-refresh fallback instead (see render()).
    if (isChunkLoadError(error) && !hasUnsavedWork()) {
      window.location.reload();
    }
  }
  render() {
    if (this.state.error) {
      if (isChunkLoadError(this.state.error)) {
        // Dirty surfaces don't auto-reload — offer a manual, opt-in refresh so
        // the user can save first. Clean surfaces reload (above) and briefly
        // show this "Updating app…" state.
        if (hasUnsavedWork()) {
          return (
            <div className="flex flex-col items-center justify-center h-64 gap-3 text-center px-4">
              <p className="text-sm text-foreground font-medium">
                Versi baharu tersedia / New version available
              </p>
              <p className="text-xs text-muted-foreground max-w-xs">
                Sila simpan kerja anda, kemudian muat semula.
                <br />
                Please save your work, then refresh.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Muat Semula / Refresh
              </button>
            </div>
          );
        }
        return (
          <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
            Updating app…
          </div>
        );
      }
      return (
        <div className="p-6 text-sm text-red-700 bg-red-50 rounded-lg border border-red-200">
          <p className="font-semibold mb-1">Something went wrong</p>
          <p className="text-xs text-red-500">{this.state.error.message}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function ResponsiveDashboard() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile ? <MobileDashboardPage shell={false} /> : <DashboardPage />;
}

function NotificationToastOverlay() {
  const { toast, dismissToast, markAsRead } = useNotificationContext();
  if (!toast) return null;
  return <NotificationToast notification={toast} onDismiss={dismissToast} onRead={markAsRead} />;
}

export default function AppShell() {
  const user = useCurrentUser();
  const location = useLocation();

  // Portal preview — standalone layout for staff to preview customer portal
  if (
    location.pathname.startsWith('/portal-preview/') &&
    (user?.role === 'ADMIN' || user?.role === 'MANAGER' || user?.role === 'SUPER_ADMIN')
  ) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/portal-preview/:jobId" element={<PortalPage preview />} />
        </Routes>
      </Suspense>
    );
  }

  // Shop floor layout — mobile card UI with bottom nav
  if (user?.role === 'LEAD' || user?.role === 'TECH') {
    const defaultPath = user?.role === 'TECH' ? '/my-tasks' : '/dashboard';
    return (
      <>
        <NotificationToastOverlay />
        <PageErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route
                path="/dashboard"
                element={
                  <RequireRole roles={['LEAD', 'TECH']}>
                    <MobileDashboardPage />
                  </RequireRole>
                }
              />
              <Route
                path="/jobs"
                element={
                  <RequireRole roles={['LEAD']}>
                    <MobileJobListPage />
                  </RequireRole>
                }
              />
              <Route
                path="/my-tasks"
                element={
                  <RequireRole roles={['LEAD', 'TECH']}>
                    <MyTasksPage />
                  </RequireRole>
                }
              />
              <Route
                path="/my-tasks/history"
                element={
                  <RequireRole roles={['LEAD', 'TECH']}>
                    <TaskHistoryPage />
                  </RequireRole>
                }
              />
              <Route
                path="/jobs/:id/floor"
                element={
                  <RequireRole roles={['LEAD', 'TECH']}>
                    <FloorViewPage />
                  </RequireRole>
                }
              />
              <Route
                path="/jobs/:id/work-order"
                element={
                  <RequireRole roles={['ADMIN', 'MANAGER', 'LEAD', 'TECH']}>
                    <WorkOrderPage />
                  </RequireRole>
                }
              />
              <Route
                path="/jobs/:id/intake"
                element={
                  <RequireRole roles={['LEAD', 'TECH']}>
                    <StageFormPage stage="CHECK_IN" />
                  </RequireRole>
                }
              />
              <Route
                path="/jobs/:id/qc"
                element={
                  <RequireRole roles={['LEAD', 'TECH']}>
                    <StageFormPage stage="QC" />
                  </RequireRole>
                }
              />
              <Route
                path="/appointments"
                element={
                  <RequireRole roles={['LEAD']}>
                    <AppointmentsPage />
                  </RequireRole>
                }
              />
              <Route path="/" element={<Navigate to={defaultPath} replace />} />
              <Route path="*" element={<Navigate to={defaultPath} replace />} />
            </Routes>
          </Suspense>
        </PageErrorBoundary>
      </>
    );
  }

  // Customer portal — no sidebar, mobile single-scroll
  if (user?.role === 'CUSTOMER') {
    return (
      <PageErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route
              path="/portal/:jobId"
              element={
                <RequireRole roles={['CUSTOMER']}>
                  <PortalPage />
                </RequireRole>
              }
            />
            <Route
              path="/portal"
              element={
                <RequireRole roles={['CUSTOMER']}>
                  <PortalJobListPage />
                </RequireRole>
              }
            />
            <Route
              path="/portal/warranty-claims"
              element={
                <RequireRole roles={['CUSTOMER']}>
                  <PortalWarrantyClaimsPage />
                </RequireRole>
              }
            />
            <Route
              path="/portal/warranty-claims/:id"
              element={
                <RequireRole roles={['CUSTOMER']}>
                  <PortalWarrantyClaimDetailPage />
                </RequireRole>
              }
            />
            <Route path="/" element={<Navigate to="/portal" replace />} />
            <Route path="*" element={<Navigate to="/portal" replace />} />
          </Routes>
        </Suspense>
      </PageErrorBoundary>
    );
  }

  // Office layout — sidebar + content
  return (
    <div className="flex h-screen overflow-hidden">
      <NotificationToastOverlay />
      <Sidebar />
      <main
        id="main-content"
        tabIndex={-1}
        className="flex-1 overflow-y-auto bg-background p-4 md:p-6 pt-[72px] md:pt-6 focus:outline-none"
      >
        <PageErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route
                path="/dashboard"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <ResponsiveDashboard />
                  </RequireRole>
                }
              />
              <Route
                path="/customers"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <CustomerListPage />
                  </RequireRole>
                }
              />
              <Route
                path="/customers/:id"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <CustomerDetailPage />
                  </RequireRole>
                }
              />
              <Route
                path="/inquiries"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <InquiryListPage />
                  </RequireRole>
                }
              />
              <Route
                path="/inquiries/new"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <NewInquiryPage />
                  </RequireRole>
                }
              />
              <Route
                path="/inquiries/:id"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <InquiryDetailPage />
                  </RequireRole>
                }
              />
              <Route
                path="/inquiries/:id/edit"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <EditInquiryPage />
                  </RequireRole>
                }
              />
              <Route
                path="/quotations"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <QuotationListPage />
                  </RequireRole>
                }
              />
              <Route
                path="/quotations/new"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <NewQuotationPage />
                  </RequireRole>
                }
              />
              <Route
                path="/quotations/:id"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <QuotationDetailPage />
                  </RequireRole>
                }
              />
              <Route
                path="/appointments"
                element={
                  <RequireRole roles={['ADMIN', 'MANAGER', 'LEAD']}>
                    <AppointmentsPage />
                  </RequireRole>
                }
              />
              <Route
                path="/jobs"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <JobListPage />
                  </RequireRole>
                }
              />
              <Route
                path="/jobs/:id"
                element={
                  <RequireRole roles={['ADMIN', 'MANAGER', 'LEAD']}>
                    <JobDetailPage />
                  </RequireRole>
                }
              />
              <Route
                path="/jobs/:id/floor"
                element={
                  <RequireRole roles={['ADMIN', 'MANAGER', 'LEAD']}>
                    <FloorViewPage />
                  </RequireRole>
                }
              />
              <Route
                path="/jobs/:id/work-order"
                element={
                  <RequireRole roles={['ADMIN', 'MANAGER', 'LEAD', 'TECH']}>
                    <WorkOrderPage />
                  </RequireRole>
                }
              />
              <Route
                path="/jobs/:id/intake"
                element={
                  <RequireRole roles={['ADMIN', 'MANAGER', 'LEAD', 'TECH']}>
                    <StageFormPage stage="CHECK_IN" />
                  </RequireRole>
                }
              />
              <Route
                path="/jobs/:id/qc"
                element={
                  <RequireRole roles={['ADMIN', 'MANAGER', 'LEAD', 'TECH']}>
                    <StageFormPage stage="QC" />
                  </RequireRole>
                }
              />
              <Route
                path="/search"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <SearchPage />
                  </RequireRole>
                }
              />
              <Route
                path="/accounting/export"
                element={
                  <RequireRole roles={['MANAGER', 'ACCOUNTANT']}>
                    <AccountingExportPage />
                  </RequireRole>
                }
              />
              <Route
                path="/finance"
                element={
                  <RequireRole roles={['ADMIN', 'MANAGER', 'ACCOUNTANT']}>
                    <FinancePage />
                  </RequireRole>
                }
              />
              <Route
                path="/finance/sales-report"
                element={<Navigate to="/finance?tab=sales" replace />}
              />
              {/* ACCOUNTANT included — keep in sync with @Roles on
                  GET /finance/salesperson/:userId (sales-by-person click-through). */}
              <Route
                path="/finance/salesperson/:userId"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'ACCOUNTANT']}>
                    <SalespersonDetailPage />
                  </RequireRole>
                }
              />
              <Route
                path="/my-tasks"
                element={
                  <RequireRole roles={['ADMIN', 'MANAGER', 'SUPER_ADMIN']}>
                    <MyTasksPage shell={false} />
                  </RequireRole>
                }
              />
              <Route
                path="/my-tasks/history"
                element={
                  <RequireRole roles={['ADMIN', 'MANAGER', 'SUPER_ADMIN']}>
                    <TaskHistoryPage shell={false} />
                  </RequireRole>
                }
              />
              <Route
                path="/pending-items"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <PendingItemsPage />
                  </RequireRole>
                }
              />
              <Route
                path="/reports/tasks"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <TaskReportPage />
                  </RequireRole>
                }
              />
              <Route
                path="/reports/job-summary"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'ACCOUNTANT']}>
                    <JobSummaryPage />
                  </RequireRole>
                }
              />
              <Route
                path="/kpi"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <KpiPage />
                  </RequireRole>
                }
              />
              <Route
                path="/settings/catalog"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <CatalogPage />
                  </RequireRole>
                }
              />
              <Route
                path="/settings/colors"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <ColorCatalogAdminPage />
                  </RequireRole>
                }
              />
              <Route
                path="/settings/clear-coats"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <ClearCoatCatalogPage />
                  </RequireRole>
                }
              />
              <Route
                path="/settings/users"
                element={
                  <RequireRole roles={['SUPER_ADMIN']}>
                    <UsersPage />
                  </RequireRole>
                }
              />
              <Route
                path="/settings/campaigns"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <CampaignsPage />
                  </RequireRole>
                }
              />
              <Route
                path="/settings/suppliers"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <SuppliersPage />
                  </RequireRole>
                }
              />
              <Route
                path="/settings/integration"
                element={
                  <RequireRole roles={['SUPER_ADMIN']}>
                    <IntegrationPage />
                  </RequireRole>
                }
              />
              <Route
                path="/settings/whatsapp"
                element={
                  <RequireRole roles={['SUPER_ADMIN']}>
                    <WhatsAppSetupPage />
                  </RequireRole>
                }
              />
              <Route
                path="/settings/whatsapp-broadcast"
                element={
                  <RequireRole roles={['SUPER_ADMIN']}>
                    <WhatsAppBroadcastPage />
                  </RequireRole>
                }
              />
              <Route
                path="/settings/version"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <VersionPage />
                  </RequireRole>
                }
              />
              {/* LEAD excluded on purpose: LEAD/TECH use the shop-floor branch
                  above, which has no /warranties route or recovery UI — this
                  table is unreachable for them (see Lessons 8/14/22). */}
              <Route
                path="/warranties"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <WarrantyListPage />
                  </RequireRole>
                }
              />
              <Route
                path="/warranty-claims"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'LEAD']}>
                    <WarrantyClaimListPage />
                  </RequireRole>
                }
              />
              <Route
                path="/warranty-claims/:id"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'LEAD']}>
                    <WarrantyClaimDetailPage />
                  </RequireRole>
                }
              />
              <Route
                path="/communication"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <CommunicationPage />
                  </RequireRole>
                }
              />
              <Route
                path="/communication/templates/:name"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN']}>
                    <TemplateEditPage />
                  </RequireRole>
                }
              />
              <Route
                path="/communication/:id"
                element={
                  <RequireRole roles={['SUPER_ADMIN', 'ADMIN', 'MANAGER']}>
                    <MessageDetailPage />
                  </RequireRole>
                }
              />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </PageErrorBoundary>
      </main>
    </div>
  );
}
